/**
 * De-identifying page-URL helpers shared by every Sentry event this app sends
 * from a handled path (reportCallableRejection, the saved-profiles near-cap
 * warning).
 *
 * The browser integration stamps the FULL page URL (query + hash), a Referer
 * header and cookies on every event. Routes here carry record ids
 * (/billing/customers/<profileId>), so an event that is meant to carry only a
 * code and a route must have those replaced before it leaves the device.
 *
 * Imports NOTHING. This file is shared by every hosting app (client,
 * client-tech, sales-client, time-client); the scope is typed structurally so
 * no bare specifier a consumer lacks can creep in (see buildIdentity.ts).
 */

/**
 * Segments that must not leave the device verbatim: anything containing a digit
 * (record ids), anything email-shaped (raw or percent-encoded `@`), and long
 * slugs (20+ chars, which is where names and titles end up).
 */
export function isIdentifyingSegment(segment: string): boolean {
  return /\d/.test(segment) || /@|%40/i.test(segment) || segment.length >= 20;
}

/**
 * Segments that look like a customer FILE rather than a route word (#8959 parity, #8935 review): a file
 * extension, whitespace, a percent-escape or non-ASCII, after decoding ("invoice.pdf", "scan%20of%20meter.jpg",
 * "cafe\u0301"). Uploaded filenames live in paths and carry no digit, so the identifying rule above misses them.
 * A malformed escape is itself a reason to template. Checked AFTER isIdentifyingSegment: a digit still wins (":id").
 */
const FILE_LIKE = /\.[A-Za-z0-9]{1,8}$|\s|%|[^\x20-\x7e]/;
export function isFileLikeSegment(segment: string): boolean {
  const decoded = decodeSegment(segment);
  return decoded === null || FILE_LIKE.test(decoded);
}

/** The segment percent-decoded, or null when an escape is malformed. */
function decodeSegment(segment: string): string | null {
  try {
    return decodeURIComponent(segment);
  } catch {
    return null;
  }
}

/**
 * One path segment with customer-looking content replaced: record ids, emails and tokens -> ":id", files -> ":file".
 * Rules run on the DECODED text, so "scan%20of%20meter.jpg" is judged as "scan of meter.jpg" (a file, no digit), not by
 * the digits of its escapes; an undecodable segment is templated as a file.
 */
export function scrubSegment(segment: string): string {
  if (!segment) return segment;
  const decoded = decodeSegment(segment);
  if (decoded === null) return ':file';
  if (isIdentifyingSegment(decoded)) return ':id';
  if (FILE_LIKE.test(decoded)) return ':file';
  return segment;
}

/**
 * The route with query, hash and identifying segments removed, so it groups and
 * holds no record identifiers.
 */
export function currentRoute(): string {
  try {
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    return (
      path
        .split('/')
        .map(scrubSegment)
        .join('/') || '/'
    );
  } catch {
    return 'unknown';
  }
}

export function currentOrigin(): string {
  try {
    return typeof window !== 'undefined' ? window.location.origin : '';
  } catch {
    return '';
  }
}

/** Origin + de-identified route: no query, no hash, no record ids. */
export function scrubbedPageUrl(): string {
  return `${currentOrigin()}${currentRoute()}`;
}

/** The slice of a Sentry event the processor rewrites. */
interface ScrubbableEvent {
  request?: {
    url?: string;
    headers?: Record<string, string>;
    query_string?: unknown;
    cookies?: unknown;
    [key: string]: unknown;
  };
  breadcrumbs?: unknown;
  [key: string]: unknown;
}

/** The slice of a Sentry scope this helper needs. */
interface ScrubbableScope {
  addEventProcessor(processor: (event: any) => any): unknown; // eslint-disable-line @typescript-eslint/no-explicit-any
}

export interface ScrubOptions {
  /**
   * Also empty the event's breadcrumbs. Navigation/fetch breadcrumbs can carry
   * record-id URLs. Off by default: callers that have not decided to change
   * their breadcrumb behaviour keep it.
   */
  clearBreadcrumbs?: boolean;
}

/**
 * Add a scope-local event processor that, for events from this scope only:
 * rewrites `request.url` to the scrubbed page URL, drops the Referer header
 * (any casing), clears `query_string`, and drops cookies. With
 * `clearBreadcrumbs`, it also empties the breadcrumbs.
 */
export function addScrubbedRequestProcessor(scope: ScrubbableScope, options: ScrubOptions = {}): void {
  const scrubbedUrl = scrubbedPageUrl();
  const { clearBreadcrumbs = false } = options;
  scope.addEventProcessor((event: ScrubbableEvent): ScrubbableEvent => {
    const withCrumbs: ScrubbableEvent = clearBreadcrumbs ? { ...event, breadcrumbs: [] } : event;
    if (!event.request) return withCrumbs;
    const headers: Record<string, string> = {};
    for (const [name, value] of Object.entries(event.request.headers ?? {})) {
      if (name.toLowerCase() !== 'referer') headers[name] = value;
    }
    const { cookies: _cookies, ...request } = event.request;
    return { ...withCrumbs, request: { ...request, url: scrubbedUrl, headers, query_string: undefined } };
  });
}

/**
 * A URL with its query string and fragment removed AND every identifying path segment
 * replaced with ":id" (same rule as the page-route scrub above). #4557: a GCS v4 signed
 * upload URL carries its credential in the query. #6018 review: capability tokens also live
 * IN paths (/invite/<token>, /public/quote/<token>, /forms/shared/<token>,
 * /public/fleet/compliance-update/<token>, /public/recruiting/referral/<token>).
 * Keeps scheme + host. Non-strings pass through.
 */
/**
 * Routes whose NEXT segment is a capability token, masked by ROUTE whatever its length or
 * alphabet (#6018 review): a future short or all-letter token must not slip past the
 * length/digit rule.
 */
const CAPABILITY_ROUTE_PREFIXES: readonly (readonly string[])[] = [
  ['invite'],
  ['public', 'quote'],
  ['forms', 'shared'],
  ['public', 'fleet', 'compliance-update'],
  ['public', 'recruiting', 'referral'],
];

/** Index of the token segment if `segments` (leading '' for an absolute path) starts with a capability route. */
function capabilityTokenIndex(segments: readonly string[]): number {
  const offset = segments[0] === '' ? 1 : 0;
  for (const prefix of CAPABILITY_ROUTE_PREFIXES) {
    if (prefix.every((part, i) => segments[offset + i] === part) && segments.length > offset + prefix.length) {
      return offset + prefix.length;
    }
  }
  return -1;
}

export function scrubUrlValue(url: unknown): unknown {
  if (typeof url !== 'string') return url;
  const cut = url.search(/[?#]/);
  const bare = cut === -1 ? url : url.slice(0, cut);
  const authority = bare.match(/^[a-z][a-z0-9+.-]*:\/\/[^/]*/i)?.[0] ?? '';
  // Drop URL userinfo ("user%40mail.com:tok3n@host"): an email and a credential live there (#8959 parity). Keep scheme + host.
  const hostStart = authority.indexOf('://') + 3;
  const origin = authority ? authority.slice(0, hostStart) + authority.slice(hostStart).slice(authority.slice(hostStart).lastIndexOf('@') + 1) : '';
  const segments = bare.slice(authority.length).split('/');
  const tokenAt = capabilityTokenIndex(segments);
  const scrubbedPath = segments.map((segment, i) => (segment && i === tokenAt ? ':id' : scrubSegment(segment))).join('/');
  return origin + scrubbedPath;
}

/** Kept for existing callers: the same scrub (it now also masks identifying path segments). */
export const stripQueryAndHash = scrubUrlValue;

/** An absolute URL anywhere in text. */
const ABSOLUTE_URL_IN_TEXT = /[a-z][a-z0-9+.-]*:\/\/[^\s"'()<>[\]{}]+/gi;
/** A path starting at the beginning, or after whitespace, "(", "=", or a quote. */
const PATH_IN_TEXT = /(^|[\s("'=])(\/[^\s"'()<>[\]{}]*)/g;

/**
 * Scrub every URL inside free text: absolute URLs anywhere ("(https://...", "url=https://...")
 * and paths after a boundary ("GET /invite/abc"). (#6018 review: splitting on spaces missed
 * URLs glued to punctuation.)
 */
export function scrubUrlsInText(text: unknown): unknown {
  if (typeof text !== 'string') return text;
  const absolute = text.replace(ABSOLUTE_URL_IN_TEXT, (url) => scrubUrlValue(url) as string);
  return absolute.replace(PATH_IN_TEXT, (_m, lead: string, path: string) => lead + (scrubUrlValue(path) as string));
}

/** The `data` keys that carry URLs: breadcrumbs (url/from/to) and span attributes. */
const URL_DATA_KEYS: readonly string[] = ['url', 'from', 'to', 'url.full', 'http.url', 'http.target', 'url.path', 'browser.web_vital.lcp.url'];
/** Span attributes that are a URL's query or fragment on their own: dropped. */
const DROPPED_DATA_KEYS: readonly string[] = ['http.query', 'http.fragment', 'url.query', 'url.fragment'];
/**
 * Attributes that hold a route the way the span name does. Sentry 11 copies the segment
 * span's name onto every child span under this key, so it gets the span-name scrub.
 */
const ROUTE_TEXT_KEYS: readonly string[] = ['sentry.segment.name'];
/**
 * Attributes Sentry 11 stamps on every streamed span from `Sentry.setUser` (the signed-in
 * staff member). An error already carries the user; a span never needs the email.
 */
const DROPPED_USER_KEYS: readonly string[] = ['user.email', 'user.name', 'user.ip_address'];
/** A string that can carry a URL's credential: absolute, or with a query / fragment. */
const URL_MARKER = /:\/\/|[?#]/;

type ValueMode = 'url' | 'text' | 'unnamed';

/** How many containers deep the attribute walk goes; anything deeper is filtered, not shipped. */
const MAX_VALUE_DEPTH = 6;
/** What replaces a container the walk will not enter: fail closed, never raw. */
const FILTERED = '[Filtered]';

/**
 * Scrub one attribute value of any shape. Sentry 11 attributes may be a string, a string
 * array, or a `{ value, unit }` object (`AttributeObject`), and breadcrumb data nests
 * freely, so this recurses (bounded) instead of looking only at top-level strings.
 *
 * `url`: the value IS a URL (named keys): whole-value scrub, query and hash removed.
 * `text`: free text that is a route or quotes URLs (span names): every URL in it scrubbed.
 * `unnamed`: a key this file does not know. Only a value that can carry a credential (an
 * absolute URL, a query or a fragment) is rewritten, so a route template like
 * `http.route: /api/v1/tickets` or a file path keeps its exact text and its grouping.
 *
 * A container deeper than `MAX_VALUE_DEPTH` is replaced by `[Filtered]`: the bound fails
 * closed, so a URL the walk does not reach is never shipped raw.
 */
function scrubValue(value: unknown, mode: ValueMode, depth = 0): unknown {
  if (typeof value === 'string') {
    if (mode === 'url') return scrubUrlValue(value);
    if (mode === 'text' || URL_MARKER.test(value)) return scrubUrlsInText(value);
    return value;
  }
  if (value === null || typeof value !== 'object') return value;
  if (depth >= MAX_VALUE_DEPTH) return FILTERED;
  if (Array.isArray(value)) return value.map((v) => scrubValue(v, mode, depth + 1));
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) out[k] = scrubValue(v, mode, depth + 1);
  return out;
}

/**
 * Copy of `data` / `attributes` with the URL-bearing keys scrubbed, the query/fragment and
 * user keys dropped, the route-text keys scrubbed like a span name, and every OTHER value
 * passed through `scrubValue` in `unnamed` mode. That last pass is what covers an attribute
 * this file does not name (a vendor key, a renamed convention, a header array quoting the
 * request URL): a value that can carry a credential cannot leave with it.
 */
function scrubUrlData(data: unknown): unknown {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return data;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
    if (DROPPED_DATA_KEYS.includes(key) || DROPPED_USER_KEYS.includes(key)) continue;
    const mode: ValueMode = URL_DATA_KEYS.includes(key) ? 'url' : ROUTE_TEXT_KEYS.includes(key) ? 'text' : 'unnamed';
    out[key] = scrubValue(value, mode);
  }
  return out;
}

/**
 * The slice of a Sentry breadcrumb `scrubBreadcrumbUrls` rewrites. No index signature:
 * Sentry's `Breadcrumb` has none, so one here would make the function unassignable to
 * `beforeBreadcrumb` under the client's CI tsconfig.
 */
interface ScrubbableBreadcrumb {
  category?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Sentry types data as { [key: string]: any }
  data?: { [key: string]: any };
}

/** Breadcrumb categories whose `data` holds request or page URLs. */
const URL_BREADCRUMB_CATEGORIES: ReadonlySet<string> = new Set(['xhr', 'fetch', 'navigation']);

/**
 * `beforeBreadcrumb` for every hosting app's `Sentry.init` (#4557): scrubs the URLs in
 * xhr / fetch / navigation breadcrumbs (query, fragment, identifying path segments).
 * Sentry's default XHR instrumentation records the full request URL, so without this a
 * photo upload's signed URL (a live write credential) rode along on the next error.
 * Returns a new breadcrumb; the input is not mutated.
 */
export function scrubBreadcrumbUrls<T extends ScrubbableBreadcrumb>(breadcrumb: T): T {
  if (!breadcrumb.category || !URL_BREADCRUMB_CATEGORIES.has(breadcrumb.category) || !breadcrumb.data) {
    return breadcrumb;
  }
  return { ...breadcrumb, data: scrubUrlData(breadcrumb.data) as ScrubbableBreadcrumb['data'] };
}

/**
 * `beforeSendSpan` (#6018 review): http.client spans carry the full request URL in their
 * name and attributes; pageload/navigation spans carry the route. Returns a copy.
 *
 * Handles BOTH span shapes. Sentry 11 streams spans: the callback receives a
 * `StreamedSpanJSON` (`name`, `attributes`, `is_segment`) and `beforeSendTransaction` is
 * never invoked, so the segment span (the former transaction, `is_segment: true`) reaches
 * this function and nothing else. The v10 static shape (`description`, `data`) is still
 * scrubbed for the spans embedded in a transaction event handled by `scrubEventUrls`.
 */
export function scrubSpan<T extends object>(span: T): T {
  type Shape = { description?: unknown; data?: unknown; name?: unknown; attributes?: unknown; links?: unknown };
  const sp = span as T & Shape;
  const out = { ...span } as T & Shape;
  if ('description' in sp) out.description = scrubUrlsInText(sp.description);
  if ('data' in sp) out.data = scrubUrlData(sp.data);
  if ('name' in sp) out.name = scrubUrlsInText(sp.name);
  if ('attributes' in sp) out.attributes = scrubUrlData(sp.attributes);
  if (Array.isArray(sp.links)) {
    out.links = sp.links.map((link) =>
      link && typeof link === 'object' && 'attributes' in link
        ? { ...link, attributes: scrubUrlData((link as { attributes?: unknown }).attributes) }
        : link
    );
  }
  return out as T;
}

/** Request headers that carry the page URL (scrubbed) or a session (dropped). */
const DROPPED_HEADERS: ReadonlySet<string> = new Set(['cookie', 'set-cookie']);
const URL_HEADERS: ReadonlySet<string> = new Set(['referer', 'referrer']);

/** Copy of `request.headers` with the cookie headers dropped and the Referer scrubbed. */
function scrubRequestHeaders(headers: unknown): unknown {
  if (!headers || typeof headers !== 'object' || Array.isArray(headers)) return headers;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(headers as Record<string, unknown>)) {
    const name = key.toLowerCase();
    if (DROPPED_HEADERS.has(name)) continue;
    out[key] = URL_HEADERS.has(name) ? scrubValue(value, 'url') : value;
  }
  return out;
}

/**
 * Event-level URLs: the page URL and Referer on `request` (`cookies` and the cookie headers dropped), the message,
 * every exception value, every breadcrumb, the transaction (route) name, the trace
 * context's attributes, and every span of a transaction event. Returns a copy.
 *
 * The browser SDK stamps `request.headers.Referer` on EVERY error event whatever the
 * `dataCollection` headers setting says (v11 `getHttpRequestData`), and a failed fetch
 * quotes its URL in the exception value, so those are scrubbed here and not left to the
 * SDK.
 */
export function scrubEventUrls<T extends object>(event: T): T {
  const e = event as T & {
    request?: { url?: unknown; query_string?: unknown; cookies?: unknown; headers?: unknown; [k: string]: unknown };
    message?: unknown;
    exception?: { values?: unknown; [k: string]: unknown };
    breadcrumbs?: unknown;
    transaction?: unknown;
    contexts?: { trace?: { data?: unknown; [k: string]: unknown }; [k: string]: unknown };
    spans?: unknown;
  };
  const out: Record<string, unknown> = { ...(event as object) };
  if (e.request) {
    const { query_string: _qs, cookies: _cookies, ...request } = e.request;
    const scrubbedRequest: Record<string, unknown> = { ...request, url: scrubUrlValue(e.request.url) };
    if ('headers' in request) scrubbedRequest.headers = scrubRequestHeaders(request.headers);
    out.request = scrubbedRequest;
  }
  if (typeof e.message === 'string') out.message = scrubUrlsInText(e.message);
  if (e.exception && Array.isArray(e.exception.values)) {
    out.exception = {
      ...e.exception,
      values: e.exception.values.map((v) =>
        v && typeof v === 'object' ? { ...v, value: scrubUrlsInText((v as { value?: unknown }).value) } : v
      ),
    };
  }
  if (Array.isArray(e.breadcrumbs)) {
    out.breadcrumbs = e.breadcrumbs.map((b) => {
      if (!b || typeof b !== 'object') return b;
      const crumb = b as { message?: unknown; data?: unknown };
      const scrubbed: { message?: unknown; data?: unknown } = { ...crumb };
      if ('message' in crumb) scrubbed.message = scrubUrlsInText(crumb.message);
      if ('data' in crumb) scrubbed.data = scrubUrlData(crumb.data);
      return scrubbed;
    });
  }
  if (typeof e.transaction === 'string') out.transaction = scrubUrlsInText(e.transaction);
  if (e.contexts?.trace) {
    out.contexts = { ...e.contexts, trace: { ...e.contexts.trace, data: scrubUrlData(e.contexts.trace.data) } };
  }
  if (Array.isArray(e.spans)) {
    out.spans = e.spans.map((sp) => (sp && typeof sp === 'object' ? scrubSpan(sp as object) : sp));
  }
  return out as T;
}

/**
 * The former `beforeSendTransaction` scrub. Sentry 11 never invokes `beforeSendTransaction`
 * (spans stream; there is no transaction event), so NO entry file wires this any more and
 * the wiring test forbids it: the segment span goes through `scrubSpan`. Kept as a named
 * alias for callers that scrub a transaction-shaped event by hand.
 */
export const scrubTransactionEvent = scrubEventUrls;

/**
 * Wrap an app's `beforeSend` so the event's own URLs are scrubbed first (#6018 review: an
 * error raised on /invite/<token> shipped the token in request.url).
 */
export function withScrubbedEventUrls<E extends object, H, R>(
  beforeSend: (event: E, hint: H) => R
): (event: E, hint: H) => R {
  return (event, hint) => beforeSend(scrubEventUrls(event), hint);
}

/**
 * Replay `beforeAddRecordingEvent` (#6018 review): network and navigation entries in a
 * replay recording ("performanceSpan" frames, and replayed breadcrumbs) carry full URLs.
 * Returns a scrubbed copy; never drops the frame.
 */
export function scrubReplayRecordingEvent<T extends object>(event: T): T {
  const ev = event as T & {
    type?: unknown;
    data?: { tag?: unknown; href?: unknown; payload?: { description?: unknown; data?: unknown; [k: string]: unknown } };
  };
  // rrweb Meta event (type 4), emitted with every full snapshot: data.href is the full page
  // URL (#6018 review, delta 1).
  if (ev.type === 4 && ev.data && typeof ev.data.href === 'string') {
    return { ...event, data: { ...ev.data, href: scrubUrlValue(ev.data.href) } } as T;
  }
  const payload = ev.data?.payload;
  if (!payload || (ev.data?.tag !== 'performanceSpan' && ev.data?.tag !== 'breadcrumb')) return event;
  return {
    ...event,
    data: {
      ...ev.data,
      payload: { ...payload, description: scrubUrlsInText(payload.description), data: scrubUrlData(payload.data) },
    },
  } as T;
}

/**
 * Global event processor for REPLAY events (#6018 review, delta 1). The SDK has no
 * `beforeSendReplay`, but a replay_event runs through the event processors, and it carries
 * the session's page URLs in `urls` (and a page `request.url`). Other events pass through.
 */
export function scrubReplayEventUrls<T extends object>(event: T): T {
  const e = event as T & { type?: unknown; urls?: unknown };
  if (e.type !== 'replay_event') return event;
  const out = scrubEventUrls(event) as T & { urls?: unknown };
  return (Array.isArray(e.urls) ? { ...out, urls: e.urls.map((u) => scrubUrlValue(u)) } : out) as T;
}
